import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

/**
 * Global Footprint — boutique posture with a Texas HQ.
 * "Coverage · Active" / "Coverage · Forthcoming" terminology used
 * instead of "satellite office" until formal addresses are confirmed.
 */
const OFFICES = [
  {
    city: "Austin",
    region: "Texas · United States",
    focus: "Headquarters · Senior Partnership · Investment Committee",
    status: "Headquarters",
    role: "hq",
  },
  {
    city: "New York",
    region: "United States",
    focus: "Capital Markets Liaison · North American LP Coverage",
    status: "Coverage · Active",
    role: "coverage",
  },
  {
    city: "London",
    region: "United Kingdom",
    focus: "European Origination · Limited Partner Coverage",
    status: "Coverage · Active",
    role: "coverage",
  },
  {
    city: "Geneva",
    region: "Switzerland",
    focus: "Private Wealth Dialogue · Stewardship Coverage",
    status: "Coverage · Active",
    role: "coverage",
  },
  {
    city: "Dubai",
    region: "United Arab Emirates · DIFC",
    focus: "Gulf Region · UAE & GCC Investor Coverage · Life Sciences Dialogue",
    status: "Coverage · Active",
    role: "coverage",
    link: { label: "For Gulf Investors →", to: "/gulf-investors" },
  },
  {
    city: "Singapore",
    region: "Singapore",
    focus: "Asia-Pacific LP Coverage · Family Office Dialogue",
    status: "Coverage · Active",
    role: "coverage",
  },
  {
    city: "Hong Kong",
    region: "Hong Kong SAR",
    focus: "Greater China Coverage · Strategic Capital Relationships",
    status: "Coverage · Active",
    role: "coverage",
  },
];

const Offices = () => {
  return (
    <section
      id="offices"
      data-testid={LANDING.offices}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
      style={{ background: "var(--kr-bg-2)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="mb-20">
          <SectionHeading
            index="18"
            label="Global Footprint"
            title={
              <>
                A{" "}
                <span className="italic text-[var(--kr-gold)]">
                  Texas-formed
                </span>{" "}
                private markets platform with international reach.
              </>
            }
            description="Kellermann Reid & Co. is being formed in Texas, with its headquarters in Austin — at the heart of the United States industrial, energy and real assets economy. The platform maintains strategic coverage across North America, Europe, the Gulf and Asia to support limited partner engagement, private wealth dialogue, capital markets relationships and strategic capital formation."
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
          {OFFICES.map((o, i) => (
            <article
              key={o.city}
              data-testid={`${LANDING.officeCardPrefix}${o.city.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative p-10 lg:p-12 border-r border-b border-[var(--kr-line)] group overflow-hidden ${
                o.role === "hq" ? "lg:col-span-2" : ""
              }`}
              style={
                o.role === "hq"
                  ? { background: "rgba(201,169,110,0.04)" }
                  : {}
              }
            >
              {/* coordinate-style decoration */}
              <span
                aria-hidden
                className="absolute top-6 right-6 font-mono-label text-[9px] text-[var(--kr-text-dim)] group-hover:text-[var(--kr-gold)] transition-colors duration-500"
              >
                {String(i + 1).padStart(2, "0")} /{" "}
                {String(OFFICES.length).padStart(2, "0")}
              </span>

              {o.role === "hq" ? (
                <div className="flex items-center gap-2 mb-6">
                  <span
                    aria-hidden
                    className="block w-[6px] h-[6px]"
                    style={{
                      background: "var(--kr-gold)",
                      transform: "rotate(45deg)",
                    }}
                  />
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    {o.status}
                  </span>
                </div>
              ) : (
                <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                  {o.status}
                </div>
              )}

              <h3 className="font-serif-display text-[2.2rem] leading-[1.1] text-[var(--kr-text)] mb-3">
                {o.city}
              </h3>
              <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[1.05rem] mb-10">
                {o.region}
              </div>

              <div className="kr-rule mb-6" />

              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
                Focus
              </div>
              <p className="text-[var(--kr-text)] text-[14px] leading-[1.65]">
                {o.focus}
              </p>
              {o.link && (
                <Link
                  to={o.link.to}
                  data-testid={`${LANDING.officeCardPrefix}${o.city.toLowerCase()}-link`}
                  className="kr-link font-mono-label text-[10px] inline-block mt-6"
                >
                  {o.link.label}
                </Link>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Offices;
